import React from "react";
import { Link } from "react-router-dom";
import { usePageTitle } from "@/lib/usePageTitle";
import { useApp } from "@/state/AppContext";
import { LEGAL_DOCS, LEGAL_EFFECTIVE_DATE } from "@/constants/legal";

/** One component for every published policy — privacy notice, terms of service, emergency
 * disclaimer — driven by `src/constants/legal.js` (principle #3: build the infrastructure the
 * feature is, not the feature).
 *
 * These pages are public and must render with no session and no tenant: Google Play requires a
 * live privacy policy URL before it will publish an app, and its crawler arrives signed out. So
 * nothing here touches `auth`, and `tenant` is read only for the language, never awaited.
 */

function pick(value, lang) {
  if (value == null) return null;
  if (typeof value === "string") return value;
  // Fall back to English rather than rendering an empty policy — an unreadable notice is a
  // compliance failure, a notice in the wrong language is merely bad.
  return value[lang] || value.en;
}

function Block({ block, lang }) {
  const content = pick(block.en !== undefined ? { en: block.en, hi: block.hi } : block, lang);

  if (block.type === "ul") {
    const items = Array.isArray(content) ? content : [];
    return (
      <ul className="mt-3 space-y-2 pl-5 text-[15px] leading-relaxed text-muted-foreground">
        {items.map((item, i) => (
          <li key={i} className="list-disc marker:text-primary/60">
            {item}
          </li>
        ))}
      </ul>
    );
  }

  if (block.type === "note") {
    return (
      <p className="mt-4 rounded-lg border-l-4 border-primary bg-primary/5 px-4 py-3 text-[15px] font-medium leading-relaxed">
        {content}
      </p>
    );
  }

  return <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">{content}</p>;
}

export default function PolicyPage({ doc }) {
  const { lang } = useApp();
  const document = LEGAL_DOCS[doc];
  usePageTitle(document ? pick(document.title, lang) : null);

  if (!document) return null;

  return (
    <div className="mx-auto max-w-3xl px-5 py-10 sm:py-14" data-testid={`policy-${doc}`}>
      <h1 className="font-heading text-3xl font-semibold tracking-tight">
        {pick(document.title, lang)}
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">Effective {LEGAL_EFFECTIVE_DATE}</p>

      {document.intro ? (
        <p className="mt-6 text-base leading-relaxed">{pick(document.intro, lang)}</p>
      ) : null}

      <div className="mt-10 space-y-10">
        {document.sections.map((section, i) => (
          <section key={i}>
            <h2 className="font-heading text-lg font-semibold">{pick(section.h, lang)}</h2>
            {section.blocks.map((block, j) => (
              <Block key={j} block={block} lang={lang} />
            ))}
          </section>
        ))}
      </div>

      <nav className="mt-14 flex flex-wrap gap-x-6 gap-y-2 border-t pt-6 text-sm text-muted-foreground">
        <Link className="hover:text-foreground" to="/privacy">
          Privacy Notice
        </Link>
        <Link className="hover:text-foreground" to="/terms">
          Terms of Service
        </Link>
        <Link className="hover:text-foreground" to="/emergency-disclaimer">
          Emergency Disclaimer
        </Link>
        <Link className="hover:text-foreground" to="/delete-account">
          Delete your account
        </Link>
      </nav>
    </div>
  );
}
